import { type FC, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import type { UserDropdownMenuProps } from './UserProfileCard.types';

export const UserDropdownMenu: FC<UserDropdownMenuProps> = ({ isOpen, onClose, items, triggerRef }) => {
	const menuRef = useRef<HTMLDivElement>(null);
	const [focusedIndex, setFocusedIndex] = useState(-1);
	const [position, setPosition] = useState({ top: 0, left: 0 });

	// Calculate position above the trigger element
	useLayoutEffect(() => {
		if (isOpen && triggerRef.current) {
			const triggerRect = triggerRef.current.getBoundingClientRect();
			const menuHeight = 260; // Approximate menu height
			const spacing = 8;

			setPosition({
				top: triggerRect.top - menuHeight - spacing,
				left: triggerRect.left,
			});
		}
	}, [isOpen, triggerRef]);

	// Handle click outside
	useEffect(() => {
		if (!isOpen) return;

		const handleClickOutside = (event: MouseEvent) => {
			const target = event.target as Node;
			const isOutsideMenu = menuRef.current && !menuRef.current.contains(target);
			const isOutsideTrigger = triggerRef.current && !triggerRef.current.contains(target);

			if (isOutsideMenu && isOutsideTrigger) {
				onClose();
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [isOpen, onClose, triggerRef]);

	// Handle keyboard navigation
	const handleKeyDown = useCallback(
		(event: KeyboardEvent) => {
			if (!isOpen) return;

			switch (event.key) {
				case 'Escape':
					event.preventDefault();
					onClose();
					triggerRef.current?.focus();
					break;

				case 'ArrowDown':
					event.preventDefault();
					setFocusedIndex((prev) => (prev < items.length - 1 ? prev + 1 : 0));
					break;

				case 'ArrowUp':
					event.preventDefault();
					setFocusedIndex((prev) => (prev > 0 ? prev - 1 : items.length - 1));
					break;

				case 'Enter':
				case ' ':
					event.preventDefault();
					if (focusedIndex >= 0 && focusedIndex < items.length) {
						items[focusedIndex].action();
						onClose();
					}
					break;

				case 'Tab':
					event.preventDefault();
					onClose();
					break;
			}
		},
		[isOpen, items, focusedIndex, onClose, triggerRef]
	);

	useEffect(() => {
		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [handleKeyDown]);

	// Reset focus when menu opens
	useEffect(() => {
		if (isOpen) {
			setFocusedIndex(0);
		} else {
			setFocusedIndex(-1);
		}
	}, [isOpen]);

	// Focus the menu when it opens
	useEffect(() => {
		if (isOpen && menuRef.current) {
			menuRef.current.focus();
		}
	}, [isOpen]);

	if (!isOpen) return null;

	const menuContent = (
		<div
			ref={menuRef}
			className='user-dropdown-menu'
			role='menu'
			aria-label='User menu'
			tabIndex={-1}
			style={{
				position: 'fixed',
				top: position.top,
				left: position.left,
			}}
		>
			<div className='user-dropdown-menu__content'>
				{items.map((item, index) => (
					<button
						key={item.id}
						className={`user-dropdown-menu__item ${
							item.variant === 'destructive' ? 'user-dropdown-menu__item--destructive' : ''
						} ${focusedIndex === index ? 'user-dropdown-menu__item--focused' : ''}`}
						role='menuitem'
						tabIndex={focusedIndex === index ? 0 : -1}
						onClick={() => {
							item.action();
							onClose();
						}}
						onMouseEnter={() => setFocusedIndex(index)}
					>
						<span className='user-dropdown-menu__item-icon'>{item.icon}</span>
						<div className='user-dropdown-menu__item-text'>
							<span className='user-dropdown-menu__item-label'>{item.label}</span>
							{item.description && <span className='user-dropdown-menu__item-description'>{item.description}</span>}
						</div>
					</button>
				))}
			</div>
		</div>
	);

	return createPortal(menuContent, document.body);
};

export default UserDropdownMenu;
